import { useQuery, UseQueryResult } from '@tanstack/react-query';
import axios from 'axios';

import { CORRELATION_ID_QUERY_PARAM_KEY } from '@zep/session-id';

import { useZepApiClient } from '../zep-api/ZepApiProvider';

import {
  type NormalizedEquipmentCatalog,
  normalizeEquipmentCatalog,
} from './normalize';
import type {
  EquipmentCatalogResponse,
  EquipmentServiceKey,
  InventoryResponse,
} from './types';

export const QUERY_KEY_EQUIPMENT_CATALOG = 'equipment-catalog';
export const QUERY_KEY_RUNTIME_EQUIPMENT_CATALOG = 'runtime-equipment-catalog';

const DEFAULT_EQUIPMENT_SERVICE: EquipmentServiceKey = 'core';
const RUNTIME_EQUIPMENT_GRANT_BATCH_SIZE = 32;
export const RUNTIME_EQUIPMENT_CATALOG_PATH = '/equipment/runtime-catalog';

/**
 * Mirrors `useCurrentAvatarService` — quiz-app iframes append
 * `zepCorrelationId` to the play-app URL, and that key is the canonical
 * marker for "running inside quiz." We parse the search string directly
 * (not `useRouter`) to side-step hydration timing and re-render churn.
 */
export function useCurrentEquipmentService(): EquipmentServiceKey {
  if (typeof window === 'undefined') return DEFAULT_EQUIPMENT_SERVICE;
  const hasCorrelationId = new URLSearchParams(window.location.search).has(
    CORRELATION_ID_QUERY_PARAM_KEY,
  );
  return hasCorrelationId ? 'quiz' : DEFAULT_EQUIPMENT_SERVICE;
}

export function useEquipmentCatalog(
  service: EquipmentServiceKey = DEFAULT_EQUIPMENT_SERVICE,
  options?: {
    enabled?: boolean;
    suspense?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    inventory?: InventoryResponse | null;
  },
): UseQueryResult<NormalizedEquipmentCatalog> {
  const client = useZepApiClient();
  const { inventory: inventoryOpt, ...restOptions } = options ?? {};
  const inventory = inventoryOpt ?? null;

  return useQuery<EquipmentCatalogResponse, Error, NormalizedEquipmentCatalog>(
    [QUERY_KEY_EQUIPMENT_CATALOG, service],
    async () => {
      const { data } = await client.get(
        `/equipment/catalog?service=${service}`,
      );
      const envelope = data?.data ?? data;
      if (envelope == null) {
        throw new Error('Equipment catalog response was empty');
      }
      return envelope as EquipmentCatalogResponse;
    },
    {
      staleTime: 1000 * 60 * 30,
      useErrorBoundary: false,
      ...restOptions,
      select: raw => normalizeEquipmentCatalog(raw, { inventory }),
    },
  );
}

export function useRuntimeEquipmentCatalog(
  grants: readonly string[],
): UseQueryResult<NormalizedEquipmentCatalog> {
  const client = useZepApiClient();

  return useQuery<EquipmentCatalogResponse, Error, NormalizedEquipmentCatalog>(
    [QUERY_KEY_RUNTIME_EQUIPMENT_CATALOG, grants],
    async () => {
      const responses = await Promise.all(
        Array.from(
          {
            length: Math.ceil(
              grants.length / RUNTIME_EQUIPMENT_GRANT_BATCH_SIZE,
            ),
          },
          (_, index) =>
            grants.slice(
              index * RUNTIME_EQUIPMENT_GRANT_BATCH_SIZE,
              (index + 1) * RUNTIME_EQUIPMENT_GRANT_BATCH_SIZE,
            ),
        ).map(async batch => {
          try {
            const { data } = await client.post(RUNTIME_EQUIPMENT_CATALOG_PATH, {
              grants: batch,
            });
            const envelope = data?.data ?? data;
            if (envelope == null) {
              throw new Error('Runtime equipment catalog response was empty');
            }
            return envelope as EquipmentCatalogResponse;
          } catch (error) {
            if (axios.isAxiosError(error) && error.config) {
              error.config.data = undefined;
            }
            throw error;
          }
        }),
      );

      const merged: EquipmentCatalogResponse = {
        catalogVersion: 0,
        categories: [],
        items: {},
      };
      const categoryIds = new Set<number>();
      for (const response of responses) {
        merged.catalogVersion = Math.max(
          merged.catalogVersion,
          response.catalogVersion,
        );
        for (const category of response.categories ?? []) {
          if (categoryIds.add(category.id)) merged.categories.push(category);
        }
        for (const [category, items] of Object.entries(response.items ?? {})) {
          const key = category as keyof EquipmentCatalogResponse['items'];
          const existingItems = merged.items[key] ?? [];
          const existingIds = new Set(existingItems.map(item => item.id));
          merged.items[key] = [
            ...existingItems,
            ...(items ?? []).filter(item => !existingIds.has(item.id)),
          ];
        }
      }
      return merged;
    },
    {
      enabled: grants.length > 0,
      staleTime: Infinity,
      useErrorBoundary: false,
      select: normalizeEquipmentCatalog,
    },
  );
}
